import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dispatch } from "redux";
import classNames from "classnames";
import { Toaster, toast } from "react-hot-toast";
import commonUtils, { defineds } from "redux/_common/utils";
import InfiniteProgress from "components/_common/InfiniteProgress";
import { IOption } from "redux/app/feedback/@types";
import { startOfYear } from "date-fns";
import Select from "react-select";
import { IStore } from "redux/store";
import toolSelectors from "redux/app/tool/selectors";
import toolActions from "redux/app/tool/actions";
import { connect, ConnectedProps } from "react-redux";
import { IVessel } from "redux/app/vessel/types";
import clsx from "clsx";
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	menuStyles,
	optionStyles,
	placeholderStyles,
} from "redux/app/feedback/utils";
import DateRangePickerVerbose from "components/_common/DateRangePickerVerbose";
import { logsPdfDefaultStaticRanges } from "redux/app/insight/utils";
import { Range, RangeKeyDict } from "react-date-range";
import ModalPortal from "components/app/_common/ModalPortal";
import { IBullMQJob } from "redux/app/tool/@types";

type OwnProps = {
	onRefreshRef?: React.MutableRefObject<(() => void) | null>;
};

type BullMQQueueProps = PropsFromRedux & OwnProps;

function BullMQQueue(props: BullMQQueueProps) {
	const [isCurrentJobExpanded, setIsCurrentJobExpanded] = useState(false);
	const [isWaitingJobsExpanded, setIsWaitingJobsExpanded] = useState(false);
	const [isCompletedJobsExpanded, setIsCompletedJobsExpanded] = useState(false);
	const { loadBullMQJobs } = props;
	const loadQueueData = useCallback(() => {
		loadBullMQJobs();
	}, [loadBullMQJobs]);



	useEffect(() => {
		loadQueueData();
	}, [loadQueueData]);

	// Expose refresh method to parent component
	useEffect(() => {
		if (props.onRefreshRef) {
			props.onRefreshRef.current = loadQueueData;
		}
	}, [loadQueueData, props.onRefreshRef]);

	// Get loading state and data from Redux
	const isLoading = props.bullMQJobsStatus?.isLoading || false;
	const queueData = props.bullMQJobs;
	const error = props.bullMQJobsStatus?.isError
		? "Failed to load BullMQ jobs"
		: null;

	// Keep filter states for UI (though they may not be used for BullMQ data yet)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [filters, setFilters] = useState<{
		status?: "all" | "success" | "failure";
		tenant?: string;
		vesselName?: string;
		logType?: string;
	}>({
		status: "all",
	});
	const [selectedTenantOption, setSelectedTenantOption] =
		useState<IOption | null>(null);
	const [selectedVesselOption, setSelectedVesselOption] =
		useState<IOption | null>(null);
	const [selectedLogOption, setSelectedLogOption] = useState<IOption | null>(
		null
	);
	const [dateRange, setDateRange] = useState<Range[]>([
		{
			startDate: startOfYear(new Date()),
			endDate: defineds.endOfToday,
			key: "selection",
		},
	]);
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

	// Load tenants on component mount
	useEffect(() => {
		props.loadTenants();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Load vessels when tenant changes
	useEffect(() => {
		if (selectedTenantOption?.value) {
			props.loadVessels(selectedTenantOption.value);
		}
	}, [selectedTenantOption]); // eslint-disable-line react-hooks/exhaustive-deps

	const formatDate = (dateString: string, needTime: boolean = true) => {
		const date = new Date(dateString);

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");

		let hours = date.getHours();
		const minutes = String(date.getMinutes()).padStart(2, "0");

		const ampm = hours >= 12 ? "PM" : "AM";
		hours = hours % 12 || 12; // convert 0 → 12 for 12 AM
		const formattedHours = String(hours).padStart(2, "0");

		return needTime
			? `${year}-${month}-${day}, ${formattedHours}:${minutes} ${ampm}`
			: `${year}-${month}-${day}`;
	};

	const formatDuration = (minutes: number | null | undefined) => {
		if (!minutes) return 0;
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
	};

	const renderCurrentJob = (job: IBullMQJob) => {
		return (
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				{/* Accordion Header */}
				<div
					className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
					onClick={() => setIsCurrentJobExpanded(!isCurrentJobExpanded)}
				>
					<div className="flex items-center gap-3">
						<h2 className="text-xl font-semibold text-gray-900">
							Current Active Job
						</h2>
						<span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
							ACTIVE
						</span>
					</div>
					<div className="flex items-center gap-4">
						<div className="text-right">
							<div className="text-sm text-gray-500">Progress</div>
							<div className="text-lg font-semibold text-gray-900">
								{job.progress}%
							</div>
						</div>
						<svg
							className={classNames("w-5 h-5 transition-transform", {
								"rotate-90": isCurrentJobExpanded,
							})}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 5l7 7-7 7"
							/>
						</svg>
					</div>
				</div>
				{/* Progress Bar */}
				<div className="px-6 pb-2">
					<div className="w-full bg-gray-200 rounded-full h-2">
						<div
							className="bg-blue-600 h-2 rounded-full transition-all duration-300"
							style={{ width: `${job.progress}%` }}
						></div>
					</div>
				</div>
				{/* Accordion Content */}
				{isCurrentJobExpanded && (
					<div className="px-6 pb-6 border-t border-gray-100">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4 pt-4">
							<div>
								<div className="text-sm font-medium text-gray-500">
									Tenant Name
								</div>
								<div className="text-base text-gray-900">
									{job.tenantName || "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Vessel Name
								</div>
								<div className="text-base text-gray-900">
									{job.vesselName || "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Code 
								</div>
								<div className="text-base text-gray-900">
									{job.code || "All Logs"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Start Date
								</div>
								<div className="text-base text-gray-900">
									{job.startDate ? formatDate(job.startDate, false) : "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									End Date
								</div>
								<div className="text-base text-gray-900">
									{job.endDate ? formatDate(job.endDate, false) : "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Total Logs
								</div>
								<div className="text-base text-gray-900">
									{job.totalLogs || job.totalPdfs || "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Processed Logs
								</div>
								<div className="text-base text-gray-900">
									{Math.round((job.progress / 100) * (job.totalLogs || job.totalPdfs || 0))  || "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Remaining Logs
								</div>
								<div className="text-base text-gray-900">
									{Math.round(((100 - job.progress) / 100) * (job.totalLogs || job.totalPdfs || 0))  || "N/A"}
								</div>
							</div>
							<div>
								<div className="text-sm font-medium text-gray-500">
									Estimated Time
								</div>
								<div className="text-base text-gray-900">
									{formatDuration(job.estimatedTimeMinutes)}
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderJobsTable = (
		jobs: IBullMQJob[],
		title: string,
		isExpanded: boolean,
		toggleFunction: () => void
	) => {
		return (
			<div className="bg-white border border-gray-200 rounded-lg">
				<div
					className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
					onClick={toggleFunction}
				>
					<div className="flex items-center gap-3">
						<h3 className="text-lg font-semibold text-gray-900">
							{title} ({jobs.length})
						</h3>
					</div>
					<svg
						className={classNames("w-5 h-5 transition-transform", {
							"rotate-90": isExpanded,
						})}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</div>

				{isExpanded && (
					<div className="overflow-x-auto">
						<InfiniteProgress isLoading={isLoading} isSpacedOut={false} />
						<table
							className={classNames(
								"w-full relative border border-gray-300 rounded-sm bp3-html-table bp3-interactive",
								{
									"animate-pulse": isLoading,
								}
							)}
						>
							<thead className="relative">
								<tr>
									<th className="text-sm text-gray-500">S.No</th>
									<th className="text-sm text-gray-500">Tenant Name</th>
									<th className="text-sm text-gray-500">Vessel Name</th>
									{!title.includes("Active") && (
										<th className="text-sm text-gray-500">Code</th>
									)}
									{title.includes("Active") ? (
										<>
											<th className="text-sm text-gray-500">Start Date</th>
											<th className="text-sm text-gray-500">End Date</th>
											<th className="text-sm text-gray-500">Total Logs</th>
											<th className="text-sm text-gray-500">Processed Logs</th>
											<th className="text-sm text-gray-500">Remaining Logs</th>
											<th className="text-sm text-gray-500">Est. Time</th>
										</>
									) : (
										<>
											<th className="text-sm text-gray-500">Start Date</th>
											<th className="text-sm text-gray-500">End Date</th>
											<th className="text-sm text-gray-500">Total Logs</th>
											{title.includes("Completed") && (
												<th className="text-sm text-center text-gray-500">
													Success-failure
												</th>
											)}
											{title.includes("Waiting") && (
												<th className="text-sm text-gray-500">Est. Time</th>
											)}
										</>
									)}
								</tr>
							</thead>
							<tbody
								className={classNames({
									"h-0 opacity-0 transition-all duration-100 max-h-0":
										isLoading,
									"h-auto opacity-100 transition-all duration-1000 delay-300 max-h-auto":
										!isLoading,
								})}
							>
								{!isLoading && (
									<>
										{jobs.length === 0 ? (
											<tr>
												<td
													colSpan={
														title.includes("Completed")
															? 8  // S.No, Tenant, Vessel, Code, Start Date, End Date, Total Logs, Success-failure
															: title.includes("Active")
															? 9  // S.No, Tenant, Vessel, Start Date, End Date, Total Logs, Processed, Remaining, Est. Time
															: title.includes("Waiting")
															? 8  // S.No, Tenant, Vessel, Code, Start Date, End Date, Total Logs, Est. Time
															: 6
													}
													className="text-center py-12"
												>
													<div className="flex flex-col items-center justify-center text-gray-500">
														<svg
															className="w-16 h-16 mb-4 text-gray-300"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={1.5}
																d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
															/>
														</svg>
														<h3 className="text-xl font-medium text-gray-900 mb-2">
															No {title} Found
														</h3>
														<p className="text-sm text-gray-500 max-w-md text-center">
															No jobs were found for the selected criteria.
														</p>
													</div>
												</td>
											</tr>
										) : (
											jobs.map((job, index) => (
												<tr
													key={`${job.jobId}-${index}`}
													className={classNames(
														"hover:bg-gray-50 transition-colors duration-150 w-full",
														job.status === "failed"
															? "bg-red-50"
															: "bg-green-50"
													)}
												>
													<td>
														<div className="text-sm font-medium text-gray-900">
															{index + 1}
														</div>
													</td>
													<td>
														<div className="text-sm font-medium text-gray-900">
															{job.tenantName || "N/A"}
														</div>
													</td>
													<td>
														<div className="text-sm font-medium text-gray-900">
															{job.vesselName || "N/A"}
														</div>
													</td>
													{!title.includes("Active") && (
														<td>
															<div className="text-sm font-medium text-gray-900">
																{job.code || "All Logs"}
															</div>
														</td>
													)}
													{title.includes("Active") ? (
														<>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{job.startDate ? formatDate(job.startDate, false) : "N/A"}
																</div>
															</td>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{job.endDate ? formatDate(job.endDate, false) : "N/A"}
																</div>
															</td>
														</>
													) : (
														<>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{job.startDate ? formatDate(job.startDate, false) : "N/A"}
																</div>
															</td>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{job.endDate ? formatDate(job.endDate, false) : "N/A"}
																</div>
															</td>
														</>
													)}
													<td>
														<div className="text-sm font-medium text-gray-900">
															{job.totalLogs || job.totalPdfs || 0}
														</div>
													</td>
													{title.includes("Completed") && (
														<>
															<td className="text-center">
																<div className="text-sm font-medium">
																	<span className="text-green-600 font-semibold">
																		{job.successCount || 0}
																	</span>
																	<span className="mx-1">-</span>
																	<span className="text-red-600 font-semibold">
																		{job.errorCount || 0}
																	</span>
																</div>
															</td>
															{/* <td>
																	<div className="text-sm font-medium text-gray-900">
																		{formatDuration(job.executionTimeMinutes)}
																	</div>
																</td> */}
														</>
													)}
													{title.includes("Active") && (
														<>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{Math.round(
																		((job.progress || 0) / 100) *
																			(job.totalLogs || job.totalPdfs || 0)
																	)}
																</div>
															</td>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{(job.totalLogs || job.totalPdfs || 0) -
																		Math.round(
																			((job.progress || 0) / 100) *
																				(job.totalLogs || job.totalPdfs || 0)
																		)}
																</div>
															</td>
															<td>
																<div className="text-sm font-medium text-gray-900">
																	{formatDuration(job.estimatedTimeMinutes)}
																</div>
															</td>
														</>
													)}
													{title.includes("Waiting") && (
														<td>
															<div className="text-sm font-medium text-gray-900">
																{formatDuration(job.estimatedTimeMinutes)}
															</div>
														</td>
													)}
												</tr>
											))
										)}
									</>
								)}
							</tbody>
						</table>
					</div>
				)}
			</div>
		);
	};

	const [showBulkRegenerateModal, setShowBulkRegenerateModal] =
		useState<boolean>(false);

	const handleClearFilters = () => {
		setSelectedTenantOption(null);
		setSelectedVesselOption(null);
		setSelectedLogOption(null);
		setDateRange([
			{
				startDate: defineds.past30days,
				endDate: defineds.endOfToday,
				key: "selection",
			},
		]);
		setFilters({ status: "all" });
	};

	const tenantOptions = useMemo(() => {
		if (!props.tenants || !props.tenants.ids.length) return [];
		return props.tenants.ids.map((id: string) => ({
			value: id,
			label: props.tenants.byIds[id].name,
		}));
	}, [props.tenants]);

	// Create vessel options from vessels store
	const vesselOptions = useMemo(() => {
		if (!props.vessels || !props.vessels.ids.length) return [];
		return props.vessels.ids.map((id: string) => ({
			value: props.vessels.byIds[id].id,
			label: props.vessels.byIds[id].vesselName,
		}));
	}, [props.vessels]);

	// Static log type options (like LogsPdfManager)
	const logTypeOptions = useMemo(
		() => [
			{ label: "Engine Log", value: "engineLogConfig" },
			{ label: "Deck Log", value: "deckLogConfig" },
			{ label: "Port Log", value: "portLogConfig" },
			{ label: "GMDSS Radio Log", value: "gmdssRadioConfig" },
			{ label: "Radar Log", value: "radarLogConfig" },
			{ label: "Compass Log", value: "compassLogConfig" },
			{ label: "Bridge Order Book", value: "bridgeOrderConfig" },
			{ label: "Engine Order Book", value: "engineOrderConfig" },
			{ label: "Medical Log", value: "medicalLogConfig" },
		],
		[]
	);

	const handleDateRange = (range: RangeKeyDict) => {
		const { selection } = range;
		setDateRange([selection]);
	};

	const handleBulkRegenerate = () => {
		// Show confirmation modal instead of directly executing
		setShowBulkRegenerateModal(true);
	};

	const handleCancelBulkRegenerate = () => {
		setShowBulkRegenerateModal(false);
	};

	const handleConfirmBulkRegenerate = async () => {
		setShowBulkRegenerateModal(false);
		setIsRefreshing(true);

		try {
			const startDate = commonUtils.correctToLocalDateISO(
				dateRange[0].startDate as Date
			);
			const endDate = commonUtils.correctToLocalDateISO(
				dateRange[0].endDate as Date,
				true
			);

			const tenantName = selectedTenantOption
				? props.tenants.byIds[selectedTenantOption.value]?.name || ""
				: "";
			const vesselId = selectedVesselOption?.value || "";
			const code = selectedLogOption?.value || "";

			// Build the URL with query parameters
			const url = new URL(
				`${commonUtils.getLogsPdfBaseUrl()}/trigger-bulk-logs`
			);
			if (tenantName) url.searchParams.append("tenantName", tenantName);
			if (vesselId) url.searchParams.append("vesselId", vesselId);
			if (code) url.searchParams.append("code", code);
			if (startDate) url.searchParams.append("startDate", startDate);
			if (endDate) url.searchParams.append("endDate", endDate);
			if (vesselId)
				url.searchParams.append(
					"vesselName",
					selectedVesselOption?.label || ""
				);
			// @ts-ignore
			url.searchParams.append("userId", props.user?.userId || "");
			url.searchParams.append("tenantId", selectedTenantOption?.value || "");
			// Make the API call
			const response = await fetch(url.toString(), {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
				},
			});

			if (response.ok) {
				toast.success("Bulk regeneration request submitted successfully");
				props.loadBullMQJobs();
			} else {
				toast.error("Failed to submit bulk regeneration request");
			}
		} catch (error) {
			console.error("Error submitting bulk regeneration request:", error);
			toast.error("Error submitting bulk regeneration request");
		} finally {
			setIsRefreshing(false);
		}
	};

	// Render the bulk regenerate confirmation modal
	const renderBulkRegenerateModal = () => {
		return (
			<ModalPortal
				isActive={showBulkRegenerateModal}
				onClose={handleCancelBulkRegenerate}
				width="400px"
			>
				<div className="bg-white rounded-lg shadow-xl p-6">
					<div className="flex items-center mb-4">
						<div className="flex-shrink-0">
							<svg
								className="w-6 h-6 text-amber-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
								/>
							</svg>
						</div>
						<div className="ml-3">
							<h3 className="text-lg font-medium text-gray-900">
								Confirm Bulk Regeneration
							</h3>
						</div>
					</div>
					<div className="mb-6">
						<p className="text-sm text-gray-600">
							You are about to regenerate Bulk PDFs based on your current filter
							criteria.
						</p>
						<p className="text-sm text-gray-600 mt-2">
							This action will submit regeneration requests for all matching
							jobs. Do you want to continue?
						</p>
					</div>
					<div className="flex justify-end gap-3">
						<button
							onClick={handleCancelBulkRegenerate}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
						>
							No, Cancel
						</button>
						<button
							onClick={handleConfirmBulkRegenerate}
							disabled={isRefreshing}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
						>
							{isRefreshing ? "Processing..." : "Yes, Regenerate"}
						</button>
					</div>
				</div>
			</ModalPortal>
		);
	};

	return (
		<div className="flex flex-col gap-6 w-full h-full">
			<Toaster
				position="top-right"
				toastOptions={{
					duration: 3000,
					style: {
						background: "#333",
						color: "#fff",
					},
				}}
			/>
			<div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
				<div className="flex justify-between items-center mb-4">
					<h3 className="text-lg font-medium text-gray-900">Bull Generate Logs</h3>
					<button
						type="button"
						onClick={handleClearFilters}
						disabled={isLoading}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
					>
						Clear Filters
					</button>
				</div>
				<div className="flex flex-wrap gap-4 items-end">
					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Tenant
						</label>
						<Select
							id="tenants"
							isClearable
							isSearchable
							isDisabled={isLoading}
							placeholder={"Search tenants"}
							unstyled
							value={selectedTenantOption}
							options={tenantOptions}
							onChange={(newValue) => {
								setSelectedTenantOption(newValue);
								setSelectedVesselOption(null); // Reset vessel when tenant changes
							}}
							className={classNames("text-sm")}
							menuPosition="fixed"
							classNames={{
								option: ({ isFocused, isSelected }) =>
									clsx(
										isFocused && optionStyles.focus,
										isSelected && optionStyles.selected,
										optionStyles.base
									),
								dropdownIndicator: () => dropdownIndicatorStyles,
								menu: () => menuStyles,
								clearIndicator: () => clearIndicatorStyles,
								placeholder: () => placeholderStyles,
								control: ({ isFocused, isDisabled }) =>
									clsx(
										isFocused ? controlStyles.focus : controlStyles.nonFocus,
										isDisabled ? controlStyles.disabled : controlStyles.base
									),
								valueContainer: ({ isDisabled }) =>
									clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
							}}
						/>
					</div>

					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Vessel
						</label>
						<Select
							id="vessels"
							isClearable
							isSearchable
							isDisabled={isLoading || !selectedTenantOption}
							placeholder={"Search vessels"}
							unstyled
							value={selectedVesselOption}
							options={vesselOptions}
							onChange={(newValue) => {
								setSelectedVesselOption(newValue);
							}}
							className={classNames("text-sm")}
							menuPosition="fixed"
							classNames={{
								option: ({ isFocused, isSelected }) =>
									clsx(
										isFocused && optionStyles.focus,
										isSelected && optionStyles.selected,
										optionStyles.base
									),
								dropdownIndicator: () => dropdownIndicatorStyles,
								menu: () => menuStyles,
								clearIndicator: () => clearIndicatorStyles,
								placeholder: () => placeholderStyles,
								control: ({ isFocused, isDisabled }) =>
									clsx(
										isFocused ? controlStyles.focus : controlStyles.nonFocus,
										isDisabled ? controlStyles.disabled : controlStyles.base
									),
								valueContainer: ({ isDisabled }) =>
									clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
							}}
						/>
					</div>

					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Log Type
						</label>
						<Select
							id="log-type"
							isClearable
							isSearchable
							isDisabled={isLoading}
							placeholder={"Select log"}
							unstyled
							value={selectedLogOption}
							options={logTypeOptions}
							onChange={(newValue) => {
								setSelectedLogOption(newValue);
							}}
							className={classNames("text-sm")}
							menuPosition="fixed"
							classNames={{
								option: ({ isFocused, isSelected }) =>
									clsx(
										isFocused && optionStyles.focus,
										isSelected && optionStyles.selected,
										optionStyles.base
									),
								dropdownIndicator: () => dropdownIndicatorStyles,
								menu: () => menuStyles,
								clearIndicator: () => clearIndicatorStyles,
								placeholder: () => placeholderStyles,
								control: ({ isFocused, isDisabled }) =>
									clsx(
										isFocused ? controlStyles.focus : controlStyles.nonFocus,
										isDisabled ? controlStyles.disabled : controlStyles.base
									),
								valueContainer: ({ isDisabled }) =>
									clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
							}}
						/>
					</div>

					<div className="w-64">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Date Range
						</label>
						<DateRangePickerVerbose
							handleOnChange={handleDateRange}
							onClose={() => setIsDatePickerOpen(false)}
							handleOpen={() => setIsDatePickerOpen(true)}
							isOpen={isDatePickerOpen}
							dateRange={dateRange}
							staticRanges={logsPdfDefaultStaticRanges}
							disabled={isLoading}
						/>
					</div>

					{/* Bulk Regenerate Button */}
					<div className="w-auto">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							&nbsp;
						</label>
						<button
							className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
							onClick={handleBulkRegenerate}
							disabled={isLoading || isRefreshing}
						>
							{isRefreshing ? <>Regenerating...</> : <>Bulk Regenerate</>}
						</button>
					</div>
				</div>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 rounded-md p-4">
					<div className="text-sm text-red-700">{error}</div>
				</div>
			)}

			{!isLoading && queueData ? (
				<>
					{/* Statistics Cards */}
					<div className="grid grid-cols-4 gap-4 mb-6">
						<div className="bg-white border border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Total Jobs</div>
							<div className="text-2xl font-semibold text-gray-900">
								{queueData.totalJobs}
							</div>
						</div>
						<div className="bg-white border border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Active Jobs</div>
							<div className="text-2xl font-semibold text-blue-600">
								{queueData.summary?.totalByStatus?.active ||
									queueData.jobs.active.length}
							</div>
						</div>
						<div className="bg-white border border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Waiting Jobs</div>
							<div className="text-2xl font-semibold text-yellow-600">
								{queueData.summary?.totalByStatus?.waiting ||
									queueData.jobs.waiting.length}
							</div>
						</div>
						<div className="bg-white border border-gray-200 rounded-lg p-4">
							<div className="text-sm text-gray-500">Completed Jobs</div>
							<div className="text-2xl font-semibold text-green-600">
								{queueData.summary?.totalByStatus?.completed ||
									queueData.jobs.completed.length}
							</div>
						</div>
					</div>

					{/* Current Job */}
					{isLoading ? (
						<div className="bg-white border border-gray-200 rounded-lg mb-6">
							<div className="p-6 animate-pulse">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<div className="h-6 bg-gray-200 rounded w-40"></div>
										<div className="h-6 bg-gray-200 rounded w-20"></div>
									</div>
									<div className="h-6 bg-gray-200 rounded w-16"></div>
								</div>
							</div>
						</div>
					) : queueData.jobs.active.length > 0 ? (
						renderCurrentJob(queueData.jobs.active[0])
					) : (
						<div className="bg-white border border-gray-200 rounded-lg mb-6">
							<div className="p-6 text-center text-gray-500">
								<h2 className="text-xl font-semibold text-gray-900 mb-2">
									No Active Job
								</h2>
								<p>There are currently no active jobs running.</p>
							</div>
						</div>
					)}

					{/* Waiting Jobs Table */}
					{renderJobsTable(
						queueData.jobs.waiting,
						"Waiting Jobs",
						isWaitingJobsExpanded,
						() => setIsWaitingJobsExpanded(!isWaitingJobsExpanded)
					)}

					{/* Completed Jobs Table */}
					{renderJobsTable(
						queueData.jobs.completed,
						"Completed Jobs",
						isCompletedJobsExpanded,
						() => setIsCompletedJobsExpanded(!isCompletedJobsExpanded)
					)}
				</>
			) : (
				<>
					{/* Loading Statistics Cards */}
					<div className="flex gap-4">
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4 animate-pulse">
							<div className="h-6 bg-gray-200 rounded mb-2"></div>
							<div className="h-8 bg-gray-200 rounded"></div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4 animate-pulse">
							<div className="h-6 bg-gray-200 rounded mb-2"></div>
							<div className="h-8 bg-gray-200 rounded"></div>
						</div>
						<div className="bg-white border flex-1 border-gray-200 rounded-lg p-4 animate-pulse">
							<div className="h-6 bg-gray-200 rounded mb-2"></div>
							<div className="h-8 bg-gray-200 rounded"></div>
						</div>
					</div>

					<div className="bg-white border border-gray-200 rounded-lg mb-6">
						<div className="p-6 animate-pulse">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="h-6 bg-gray-200 rounded w-40"></div>
									<div className="h-6 bg-gray-200 rounded w-20"></div>
								</div>
								<div className="h-6 bg-gray-200 rounded w-16"></div>
							</div>
						</div>
					</div>

					{/* Loading Table */}
					{renderJobsTable([], "Loading Jobs", true, () => {})}
				</>
			)}

			{renderBulkRegenerateModal()}
		</div>
	);
}

function mapStateToProps(store: IStore) {
	const toolStore = (store as any).app.tool;
	const authStore = store.app.auth;
	return {
		user: authStore?.user || null,
		bullMQJobs: toolSelectors.getBullMQJobs(toolStore),
		bullMQJobsStatus: toolSelectors.jobToLoadBullMQJobs(toolStore),
		vessels: toolStore?.vessels || { byIds: {}, ids: [] },
		tenants: toolStore?.tenants || { byIds: {}, ids: [] },
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadBullMQJobs() {
			dispatch(toolActions.commands.toolBullMQJobsLoad());
		},
		loadTenants() {
			dispatch(toolActions.commands.toolTenantListLoad());
		},
		loadVessels(tenantId: string) {
			dispatch(toolActions.commands.toolTenantSpecificVesselLoad(tenantId));
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector> & {
	vessels?: {
		byIds: { [vesselId: string]: IVessel };
		ids: string[];
	};
};
export default reduxConnector(BullMQQueue);
